import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DatePicker } from '../ui/date-picker';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  CreditCard,
  Lock,
  AlertTriangle,
  Building2,
  Info,
} from 'lucide-react';
import { useFormatting } from '@/hooks/useFormatting';
import { useApp } from '@/contexts/AppContext';
import { useToast } from '@/hooks/use-toast';
import { BANK_ACCOUNTS } from '@/data/financial-systems';
import { updateBankAccount } from '@/api/services/bankAccountService';
import { mapBankAccountToUpdateRequest } from '@/api/mappers/bankAccountMapper';
import {
  getBankAccountsGraphql,
  getRestrictedFundsGraphql,
  getCreditFacilitiesGraphql,
  getBankAccountTotals,
} from '@/api/services/bankAccountGraphqlService';
import type { BankAccountViewTotals } from '@/api/services/bankAccountGraphqlService';
import { ApiError } from '@/api/client';
import type {
  BankAccount,
  CreditFacility,
  CreditFacilityType,
  ModeType,
  RestrictedFund,
  RestrictionType,
} from '@/types';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function facilityTypeLabel(t: CreditFacilityType | undefined): string {
  if (!t) return 'facility';
  if (t === 'overdraft') return 'Overdraft';
  return 'facility';
}

function restrictionTypeLabel(t: RestrictionType): string {
  const labels: Record<RestrictionType, string> = {
    client_money: 'Client Money / Trust Fund',
    fixed_term: 'Fixed Term Deposit',
    internal_reserve: 'Internal Reserve',
  };
  return labels[t];
}

function restrictionTypeBadgeColour(t: RestrictionType): string {
  const colours: Record<RestrictionType, string> = {
    client_money: 'bg-red-100 text-red-800',
    fixed_term: 'bg-blue-100 text-blue-800',
    internal_reserve: 'bg-green-100 text-green-700',
  };
  return colours[t];
}

function availableCash(account: BankAccount): number {
  const restricted = (account.restrictedFunds ?? []).reduce((s, r) => s + r.restrictedAmount, 0);
  const facilityHeadroomTotal = (account.creditFacilities ?? []).reduce((s, f) => {
    if (f.includeInAvailableCash === false) return s;
    return s + (f.facilityLimit - f.facilityDrawn);
  }, 0);
  return account.balance - restricted + facilityHeadroomTotal;
}

function totalRestricted(account: BankAccount): number {
  return (account.restrictedFunds ?? []).reduce((s, r) => s + r.restrictedAmount, 0);
}

function headroom(account: BankAccount): number {
  return (account.creditFacilities ?? []).reduce((s, f) => {
    if (f.includeInAvailableCash === false) return s;
    return s + (f.facilityLimit - f.facilityDrawn);
  }, 0);
}

function formatDateShort(d: Date | undefined): string {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Blank templates ──────────────────────────────────────────────────────────

function blankFacility(): CreditFacility {
  return {
    id: `f-${Date.now()}`,
    type: 'overdraft',
    facilityLimit: 0,
    facilityDrawn: 0,
    includeInAvailableCash: true,
  };
}

function blankRestriction(): Omit<RestrictedFund, 'id'> {
  return {
    restrictionType: 'client_money',
    label: '',
    restrictedAmount: 0,
  };
}

// ─── Facility row ─────────────────────────────────────────────────────────────

interface FacilityRowProps {
  facility: CreditFacility;
  onUpdate: (updated: CreditFacility) => void;
  onRemove: () => void;
  onSave: () => void;
  isSaving: boolean;
  isNew?: boolean;
  onEditComplete?: () => void;
}

function FacilityRow({ facility, onUpdate, onRemove, onSave, isSaving, isNew, onEditComplete }: FacilityRowProps) {
  const { formatCurrency } = useFormatting();
  const [editing, setEditing] = useState(!!isNew);
  const [removeDialog, setRemoveDialog] = useState(false);

  if (!editing) {
    return (
      <>
        <div className="flex items-center gap-3 py-2 border-b border-border last:border-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                {facilityTypeLabel(facility.type)}
              </span>
            </div>
            <div className="flex items-center gap-4 mt-0.5 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{formatCurrency(facility.facilityLimit, 'full')} limit</span>
              <span>Headroom: {formatCurrency(facility.facilityLimit - facility.facilityDrawn, 'full')}</span>
            </div>
          </div>
          <div className="flex gap-1 shrink-0">
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="h-7 px-2 text-xs">Edit</Button>
            <Button variant="ghost" size="sm" onClick={() => setRemoveDialog(true)} className="h-7 px-2 text-xs text-destructive hover:text-destructive">
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>

        <AlertDialog open={removeDialog} onOpenChange={setRemoveDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove credit facility?</AlertDialogTitle>
              <AlertDialogDescription>
                This will remove the {facilityTypeLabel(facility.type).toLowerCase()} configuration. The facility headroom will no longer be included in available cash calculations.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={onRemove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Remove facility
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3 my-1">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Facility type</Label>
          <Select
            value={facility.type}
            onValueChange={(v) => onUpdate({ ...facility, type: v as CreditFacilityType })}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="overdraft">Overdraft</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Facility limit</Label>
          <Input
            className="h-8 text-xs"
            type="number"
            min={0}
            value={facility.facilityLimit || ''}
            onChange={(e) => onUpdate({ ...facility, facilityLimit: Number.parseFloat(e.target.value) || 0 })}
            placeholder="0"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={() => { if (isNew) onRemove(); else { setEditing(false); onEditComplete?.(); } }}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          className="h-7 text-xs"
          onClick={() => {
            setEditing(false);
            onEditComplete?.();
            onSave();
          }}
          disabled={facility.facilityLimit <= 0 || isSaving}
        >
          {isSaving ? 'Saving…' : 'Save facility'}
        </Button>
      </div>
    </div>
  );
}

// ─── Restriction row ──────────────────────────────────────────────────────────

interface RestrictionRowProps {
  restriction: RestrictedFund;
  onUpdate: (updated: RestrictedFund) => void;
  onRemove: () => void;
  onSave: () => void;
  isSaving: boolean;
  isNew?: boolean;
  onEditComplete?: () => void;
  mode: ModeType;
}

function RestrictionRow({ restriction, onUpdate, onRemove, onSave, isSaving, isNew, onEditComplete, mode }: RestrictionRowProps) {
  const { formatCurrency } = useFormatting();
  const [editing, setEditing] = useState(!!isNew);

  if (!editing) {
    return (
      <div className="flex items-center gap-3 py-2 border-b border-border last:border-0">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${restrictionTypeBadgeColour(restriction.restrictionType)}`}>
              {restrictionTypeLabel(restriction.restrictionType)}
            </span>
          </div>
          <p className="text-sm mt-0.5 text-foreground">{restriction.label}</p>
          <div className="flex items-center gap-4 mt-0.5 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{formatCurrency(restriction.restrictedAmount, 'full')}</span>
            {restriction.releaseDate && (
              <span>Releases {formatDateShort(restriction.releaseDate)}</span>
            )}
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="h-7 px-2 text-xs">Edit</Button>
          <Button variant="ghost" size="sm" onClick={onRemove} className="h-7 px-2 text-xs text-destructive hover:text-destructive">
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    );
  }

  const showReleaseDate = restriction.restrictionType === 'fixed_term';

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3 my-1">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Restriction type</Label>
          <Select
            value={restriction.restrictionType}
            onValueChange={(v) => onUpdate({ ...restriction, restrictionType: v as RestrictionType })}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="client_money">Client Money / Trust Fund</SelectItem>
              <SelectItem value="fixed_term">Fixed Term Deposit</SelectItem>
              <SelectItem value="internal_reserve">Internal Reserve</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Restricted amount</Label>
          <Input
            className="h-8 text-xs"
            type="number"
            min={0}
            value={restriction.restrictedAmount || ''}
            onChange={(e) => onUpdate({ ...restriction, restrictedAmount: Number.parseFloat(e.target.value) || 0 })}
            placeholder="0.00"
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Label / description</Label>
        <Input
          className="h-8 text-xs"
          value={restriction.label}
          onChange={(e) => onUpdate({ ...restriction, label: e.target.value })}
          placeholder={mode === 'demo' ? 'e.g. Thornton Developments client deposit' : ''}
        />
      </div>
      {showReleaseDate && (
        <div className="flex items-center gap-2">
          <Label className="text-xs whitespace-nowrap">Release date</Label>
          <DatePicker
            className="w-36"
            value={restriction.releaseDate ? new Date(restriction.releaseDate) : undefined}
            onChange={(date) => onUpdate({ ...restriction, releaseDate: date })}
          />
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={() => { if (isNew) onRemove(); else { setEditing(false); onEditComplete?.(); } }}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          className="h-7 text-xs"
          onClick={() => {
            setEditing(false);
            onEditComplete?.();
            onSave();
          }}
          disabled={!restriction.label || restriction.restrictedAmount <= 0 || isSaving}
        >
          {isSaving ? 'Saving…' : 'Save restriction'}
        </Button>
      </div>
    </div>
  );
}

// ─── Account card (Zone 1 — connected) ───────────────────────────────────────

interface AccountCardProps {
  account: BankAccount;
  onSave: (updated: BankAccount) => Promise<void>;
  mode: ModeType;
}

function AccountCard({ account, onSave, mode }: AccountCardProps) {
  const { formatCurrency } = useFormatting();
  const [local, setLocal] = useState<BankAccount>(account);
  const [facilityOpen, setFacilityOpen] = useState(!!((account.creditFacilities ?? []).length));
  const [restrictionsOpen, setRestrictionsOpen] = useState(!!(account.restrictedFunds?.length));
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newFacilityIds, setNewFacilityIds] = useState<Set<string>>(new Set());
  const [newRestrictionIds, setNewRestrictionIds] = useState<Set<string>>(new Set());

  function persistAccount(updated: BankAccount) {
    setIsSaving(true);
    onSave(updated)
      .then(() => { setIsDirty(false); })
      .catch(() => { /* stay in current state */ })
      .finally(() => { setIsSaving(false); });
  }

  // ── Facility CRUD ──

  function addFacility() {
    const newF = blankFacility();
    setLocal(prev => ({ ...prev, creditFacilities: [...(prev.creditFacilities ?? []), newF] }));
    setNewFacilityIds(prev => new Set(prev).add(newF.id));
    setFacilityOpen(true);
    setIsDirty(true);
  }

  function clearNewFacilityId(id: string) {
    setNewFacilityIds(prev => { const next = new Set(prev); next.delete(id); return next; });
  }

  function updateFacility(id: string, updated: CreditFacility) {
    setLocal(prev => ({
      ...prev,
      creditFacilities: (prev.creditFacilities ?? []).map(f => f.id === id ? updated : f),
    }));
    setIsDirty(true);
  }

  function removeFacility(id: string) {
    const wasNew = newFacilityIds.has(id);
    const remaining = (local.creditFacilities ?? []).filter(f => f.id !== id);
    const updated: BankAccount = { ...local, creditFacilities: remaining.length > 0 ? remaining : undefined };
    setLocal(updated);
    clearNewFacilityId(id);
    if (wasNew) {
      const hasOtherUnsaved = newRestrictionIds.size > 0 || Array.from(newFacilityIds).some(fid => fid !== id);
      setIsDirty(hasOtherUnsaved);
      return;
    }
    setIsDirty(true);
    persistAccount(updated);
  }

  function saveFacility() {
    persistAccount(local);
  }

  // ── Restriction CRUD ──

  function addRestriction() {
    const newR: RestrictedFund = { ...blankRestriction(), id: `r-${Date.now()}` };
    setLocal(prev => ({ ...prev, restrictedFunds: [...(prev.restrictedFunds ?? []), newR] }));
    setNewRestrictionIds(prev => new Set(prev).add(newR.id));
    setRestrictionsOpen(true);
    setIsDirty(true);
  }

  function clearNewRestrictionId(id: string) {
    setNewRestrictionIds(prev => { const next = new Set(prev); next.delete(id); return next; });
  }

  function updateRestriction(id: string, updated: RestrictedFund) {
    setLocal(prev => ({
      ...prev,
      restrictedFunds: (prev.restrictedFunds ?? []).map(r => r.id === id ? updated : r),
    }));
    setIsDirty(true);
  }

  function removeRestriction(id: string) {
    const wasNew = newRestrictionIds.has(id);
    const updated = { ...local, restrictedFunds: (local.restrictedFunds ?? []).filter(r => r.id !== id) };
    setLocal(updated);
    clearNewRestrictionId(id);
    if (wasNew) {
      const hasOtherUnsaved = newFacilityIds.size > 0 || Array.from(newRestrictionIds).some(rid => rid !== id);
      setIsDirty(hasOtherUnsaved);
      return;
    }
    setIsDirty(true);
    persistAccount(updated);
  }

  function saveRestriction() {
    persistAccount(local);
  }

  const facilities = local.creditFacilities ?? [];
  const restricted = totalRestricted(local);
  const facilityHeadroomVal = headroom(local);
  const available = availableCash(local);

  const canAddFacility = mode !== 'live' || facilities.length === 0;

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Building2 className="h-4 w-4 text-primary" />
            </div>
            <div>
              <CardTitle className="text-base">{local.name}</CardTitle>
              <p className="text-sm text-muted-foreground">{local.bank} · {local.currency ?? 'GBP'}</p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-xs text-muted-foreground">Ledger balance</p>
            <p className="text-lg font-semibold tabular-nums">{formatCurrency(local.balance, 'full')}</p>
          </div>
        </div>

        {/* Available cash summary bar */}
        <div className="mt-3 grid grid-cols-3 gap-2 rounded-lg border border-border bg-muted/40 p-3 text-center">
          <div>
            <p className="text-xs text-muted-foreground">Restricted</p>
            <p className={`text-sm font-semibold tabular-nums ${restricted > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
              {restricted > 0 ? `−${formatCurrency(restricted, 'full')}` : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Facility headroom</p>
            <p className={`text-sm font-semibold tabular-nums ${facilityHeadroomVal > 0 ? 'text-emerald-600' : 'text-muted-foreground'}`}>
              {facilityHeadroomVal > 0 ? `+${formatCurrency(facilityHeadroomVal, 'full')}` : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground font-medium">Available cash</p>
            <p className="text-sm font-bold tabular-nums text-primary">{formatCurrency(available, 'full')}</p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">

        {/* ── Credit facilities ── */}
        <Collapsible open={facilityOpen} onOpenChange={setFacilityOpen}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium bg-muted/50 hover:bg-muted transition-colors"
            >
              <span className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
                Credit facilities
                {facilities.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {facilities.length} facilit{facilities.length !== 1 ? 'ies' : 'y'} · {formatCurrency(facilities.reduce((s, f) => s + f.facilityLimit, 0), 'full')} limit
                  </Badge>
                )}
              </span>
              {facilityOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent className="mt-2 px-1 space-y-1">
            {facilities.length === 0 ? (
              <div className="flex items-center justify-between rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground">
                <span>No credit facility configured for this account</span>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addFacility}>
                  <Plus className="h-3 w-3 mr-1" /> Add facility
                </Button>
              </div>
            ) : (
              <>
                <div className="divide-y divide-border rounded-lg border border-border bg-background px-3">
                  {facilities.map(f => (
                    <FacilityRow
                      key={f.id}
                      facility={f}
                      onUpdate={(updated) => updateFacility(f.id, updated)}
                      onRemove={() => removeFacility(f.id)}
                      onSave={saveFacility}
                      isSaving={isSaving}
                      isNew={newFacilityIds.has(f.id)}
                      onEditComplete={() => clearNewFacilityId(f.id)}
                    />
                  ))}
                </div>
                {canAddFacility && (
                  <div className="flex justify-end pt-1">
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addFacility}>
                      <Plus className="h-3 w-3 mr-1" /> Add facility
                    </Button>
                  </div>
                )}
              </>
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* ── Restricted funds ── */}
        <Collapsible open={restrictionsOpen} onOpenChange={setRestrictionsOpen}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium bg-muted/50 hover:bg-muted transition-colors"
            >
              <span className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-muted-foreground" />
                Restricted funds
                {(local.restrictedFunds?.length ?? 0) > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {local.restrictedFunds!.length} restriction{local.restrictedFunds!.length !== 1 ? 's' : ''} · {formatCurrency(restricted, 'full')} excluded
                  </Badge>
                )}
              </span>
              {restrictionsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent className="mt-2 px-1 space-y-1">
            {(local.restrictedFunds?.length ?? 0) === 0 ? (
              <div className="flex items-center justify-between rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground">
                <span>No restricted funds on this account</span>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addRestriction}>
                  <Plus className="h-3 w-3 mr-1" /> Add restriction
                </Button>
              </div>
            ) : (
              <>
                <div className="divide-y divide-border rounded-lg border border-border bg-background px-3">
                  {local.restrictedFunds!.map(r => (
                    <RestrictionRow
                      key={r.id}
                      restriction={r}
                      onUpdate={(updated) => updateRestriction(r.id, updated)}
                      onRemove={() => removeRestriction(r.id)}
                      onSave={saveRestriction}
                      isSaving={isSaving}
                      isNew={newRestrictionIds.has(r.id)}
                      onEditComplete={() => clearNewRestrictionId(r.id)}
                      mode={mode}
                    />
                  ))}
                </div>
                <div className="flex justify-end pt-1">
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addRestriction}>
                    <Plus className="h-3 w-3 mr-1" /> Add restriction
                  </Button>
                </div>
              </>
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* ── Unsaved changes indicator ── */}
        {isDirty && (
          <div className="flex items-center pt-1 border-t border-border">
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Info className="h-3 w-3" />
              Unsaved changes on this account
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function BankAccountsPage() {
  const { mode } = useApp();
  const { toast } = useToast();
  const { formatCurrency } = useFormatting();

  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverTotals, setServerTotals] = useState<BankAccountViewTotals | null>(null);

  const refreshFromServer = useCallback(async () => {
    const [accountsResult, restrictedResult, facilitiesResult, totals] = await Promise.all([
      getBankAccountsGraphql(1000),
      getRestrictedFundsGraphql(undefined, 1000),
      getCreditFacilitiesGraphql(undefined, 1000),
      getBankAccountTotals(),
    ]);

    const restrictedByAccount = new Map<number, RestrictedFund[]>();
    for (const edge of restrictedResult.edges) {
      const rf = edge.node;
      const bankAccountId = rf.bankAccountId;
      if (!restrictedByAccount.has(bankAccountId)) {
        restrictedByAccount.set(bankAccountId, []);
      }
      restrictedByAccount.get(bankAccountId)!.push({
        id: String(rf.id),
        restrictionType: rf.restrictionType as RestrictionType,
        label: rf.label,
        restrictedAmount: rf.restrictedAmount,
        releaseDate: rf.releaseDate ? new Date(rf.releaseDate) : undefined,
      });
    }

    const facilitiesByAccount = new Map<number, CreditFacility[]>();
    for (const edge of facilitiesResult.edges) {
      const cf = edge.node;
      const bankAccountId = cf.bankAccountId;
      if (!facilitiesByAccount.has(bankAccountId)) {
        facilitiesByAccount.set(bankAccountId, []);
      }
      facilitiesByAccount.get(bankAccountId)!.push({
        id: String(cf.id),
        type: cf.type as CreditFacilityType,
        facilityLimit: cf.facilityLimit,
        facilityDrawn: cf.facilityDrawn,
        facilityReviewDate: cf.facilityReviewDate ? new Date(cf.facilityReviewDate) : undefined,
        securedOn: cf.securedOn ?? undefined,
      });
    }

    const mapped: BankAccount[] = accountsResult.edges.map((edge) => {
      const a = edge.node;
      const numericId = Number(a.id);
      return {
        id: String(a.id),
        name: a.name,
        bank: a.bank,
        balance: a.balance,
        currency: a.currencyCode,
        type: a.accountType ?? undefined,
        dataSourceName: a.dataSourceName ?? undefined,
        creditFacilities: facilitiesByAccount.get(numericId) ?? undefined,
        restrictedFunds: restrictedByAccount.get(numericId) ?? undefined,
      };
    });

    setAccounts(mapped);
    setServerTotals(totals);
  }, []);

  useEffect(() => {
    if (mode === 'live') {
      setIsLoading(true);
      setError(null);
      refreshFromServer()
        .catch((err) => {
          setError(err instanceof Error ? err.message : 'Failed to fetch bank accounts');
          setAccounts([]);
          setServerTotals(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setAccounts(BANK_ACCOUNTS.map(a => ({ ...a })));
      setServerTotals(null);
      setError(null);
    }
  }, [mode, refreshFromServer]);

  async function handleSave(updated: BankAccount): Promise<void> {
    if (mode === 'live') {
      const numericId = Number(updated.id);
      if (Number.isNaN(numericId)) return;
      const request = mapBankAccountToUpdateRequest(updated);
      await updateBankAccount(numericId, request)
        .catch((err) => {
          console.error('Failed to update bank account:', err);
          const description = err instanceof ApiError
            ? err.message
            : 'Failed to save bank account changes. Please try again.';
          toast({ title: 'Error', description, variant: 'destructive' });
          throw err;
        });

      await refreshFromServer();
      toast({ title: 'Saved', description: `Bank account "${updated.name}" updated successfully.` });
    } else {
      setAccounts(prev => prev.map(a => a.id === updated.id ? updated : a));
    }
  }

  const totalBalance = serverTotals ? serverTotals.totalLedgerBalance : accounts.reduce((s, a) => s + a.balance, 0);
  const totalRestrictedAll = serverTotals ? serverTotals.totalRestricted : accounts.reduce((s, a) => s + totalRestricted(a), 0);
  const totalHeadroomAll = serverTotals ? serverTotals.totalHeadroom : accounts.reduce((s, a) => s + headroom(a), 0);
  const totalAvailable = serverTotals ? serverTotals.totalAvailableCash : totalBalance - totalRestrictedAll + totalHeadroomAll;

  return (
    <div className="p-6 space-y-6 max-w-4xl">



      {/* Organisation-level summary */}
      <Card className="bg-muted/30">
        <CardContent className="py-4">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-xs text-muted-foreground">Accounts</p>
              <p className="text-xl font-bold">{accounts.length}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total ledger balance</p>
              <p className="text-xl font-bold tabular-nums">{formatCurrency(totalBalance, 'full')}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total restricted</p>
              <p className="text-xl font-bold tabular-nums text-red-600">
                {totalRestrictedAll > 0 ? `−${formatCurrency(totalRestrictedAll, 'full')}` : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Total available cash</p>
              <p className="text-xl font-bold tabular-nums text-primary">{formatCurrency(totalAvailable, 'full')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info banner */}
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <p className="font-medium">Bank accounts are connected via your configured data sources</p>
          <p className="text-xs mt-0.5 text-amber-700">
            To add or remove accounts, update your data source connections in System Settings → Data Source Config. Facility and restriction details configured here are supplementary — they do not affect the underlying connection.
          </p>
        </div>
      </div>

      {/* ── Connected accounts ── */}
      <div className="space-y-4">
        {isLoading && (
          <Card className="bg-muted/30">
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              Loading bank accounts…
            </CardContent>
          </Card>
        )}

        {error && (
          <Card className="border-destructive bg-destructive/5">
            <CardContent className="py-4 text-sm text-destructive flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {error}
            </CardContent>
          </Card>
        )}

        {!isLoading && !error && accounts.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="p-4 bg-muted rounded-full mb-4">
                <Building2 className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No bank accounts connected</h3>
              <p className="text-muted-foreground text-center max-w-md">
                Bank accounts are connected via your configured data sources. To add or remove accounts, update your data source connections in System Settings → Data Source Config.
              </p>
            </CardContent>
          </Card>
        )}

        {!isLoading && !error && accounts.map(account => (
          <AccountCard
            key={account.id}
            account={account}
            onSave={handleSave}
            mode={mode}
          />
        ))}
      </div>

    </div>
  );
}
